import { useGLTF, useTexture } from '@react-three/drei';
import { DecalGeometry } from 'three/examples/jsm/geometries/DecalGeometry';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

import { useRecoilValue } from 'recoil';
import { logoTransformState } from 'state/langState';
import { getSafeTransform } from 'utils/getText';

export default function MugDecalLogo({ logo, product }) {
  const { scene, nodes } = useGLTF('/obj/mug-cup.glb');

  const texture = useTexture(logo);

  const transforms = useRecoilValue(logoTransformState);

  const transform = getSafeTransform(transforms, product);

  const geometryRef = useRef(null);

  const [decalGeometry, setDecalGeometry] = useState(null);

  /* ===============================
     TARGET MESH
  =============================== */

  const targetMesh = nodes.Material2;

  /* ===============================
     TEXTURE 설정 (정상 패턴)
  =============================== */

  useEffect(() => {
    if (!texture) return;

    texture.flipY = false;

    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;

    texture.anisotropy = 8;

    texture.needsUpdate = true;
  }, [texture]);

  /* ===============================
     DECAL 생성
  =============================== */

  useEffect(() => {
    if (!targetMesh) return;

    targetMesh.updateWorldMatrix(true, true);

    const { x, y } = transform.position;

    const depth = transform.depth;

    const scale = transform.scale;

    /*
      Ray
    */

    const rayOrigin = new THREE.Vector3(-x, y, -5);

    const direction = new THREE.Vector3(0, 0, 1);

    const raycaster = new THREE.Raycaster(rayOrigin, direction);

    const intersects = raycaster.intersectObject(targetMesh, true);

    if (!intersects.length) return;

    const hit = intersects[0];

    /*
      normal
    */

    const normal = hit.face.normal.clone();

    normal.transformDirection(hit.object.matrixWorld);

    /*
      position
    */

    const finalPosition = hit.point.clone().add(normal.multiplyScalar(depth));

    /*
      rotation
      += 금지 (누적 버그)
    */

    const quaternion = new THREE.Quaternion();

    quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);

    const rotation = new THREE.Euler().setFromQuaternion(quaternion);

    rotation.z = Math.PI;

    /*
      size
    */

    const size = new THREE.Vector3(scale, scale, scale);

    const geometry = new DecalGeometry(
      targetMesh,
      finalPosition,
      rotation,
      size
    );

    /*
      dispose 안정화
    */

    if (geometryRef.current) {
      geometryRef.current.dispose();
    }

    geometryRef.current = geometry;

    setDecalGeometry(geometry);

    return () => {
      if (geometryRef.current) {
        geometryRef.current.dispose();
        geometryRef.current = null;
      }
    };
  }, [
    targetMesh,
    transform.position.x,
    transform.position.y,
    transform.depth,
    transform.scale,
  ]);

  /* ===============================
     RENDER
  =============================== */

  return (
    <>
      <primitive object={scene} scale={18} position={[-1, -1, 1]} />

      {decalGeometry && (
        <mesh geometry={decalGeometry}>
          <meshStandardMaterial
            map={texture}
            transparent
            depthWrite={false}
            polygonOffset
            polygonOffsetFactor={-4}
          />
        </mesh>
      )}
    </>
  );
}

useGLTF.preload('/obj/mug-cup.glb');
